import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {graphql} from 'gatsby';

import Navbar from 'views/Navbar';
import Top from 'views/Top';
import Footer from 'views/Footer';
import * as Sections from 'views/Sections';
import SEO from 'components/SEO';
import LanguageSelector from 'components/LanguageSelector';

import 'utils/fixFontAwesome';
import breakDownAllNodes from 'utils/breakDownAllNodes';
import fileNameToSectionName from 'utils/fileNameToSectionName';
// import CookieConsentComponent from '../cookies/CookieConsentComponent';

import '../style/main.scss';

/**
 * get file name list from content/sections folder
 */
export const query = graphql`
  query IndexQuery($langKey: String!) {
    site {
      siteMetadata {
        keywords
        description
      }
    }
    allMarkdownRemark(
      filter: { fields: { langKey: { eq: $langKey } } }
      sort: { order: ASC, fields: [fields___directoryName, fields___fileName] }
    ) {
      nodes {
        frontmatter {
          brand
          anchor
          anchorText
          partners {
            href
            imageFileName
          }
          content
          copyright
          header
          subheader
          email
          imageFileName
          jumpToAnchor
          jumpToAnchorText
          fareHarbor
          hotNewsHeader
          hotNewsContent
          buttonClose
          buttonBookDives
          courseContains
          courseBullets
          courseMinimumAge
          coursePrerequisites
          courseAcademicSessions
          coursePoolSessions
          courseDives
          depth
          meter
          financedBy
          menuText
          courses {
            content
            courseData {
              title
              description
              price
              fareHabor
              courseMinimumAge
              coursePrerequisites
              courseAcademicSessions
              coursePoolSessions
              courseDives
            }
            header
            subheader
            imageFileNameDetail
            imageFileName
          }
          coursesBeginnerHeader
          coursesBeginnerSubheader
          coursesBeginner {
            content
            courseData {
              title
              description
              price
              fareHabor
              courseMinimumAge
              coursePrerequisites
              courseAcademicSessions
              coursePoolSessions
              courseDives
            }
            header
            subheader
            imageFileNameDetail
            imageFileName
          }
          coursesAdvancedHeader
          coursesAdvancedSubheader
          coursesAdvanced {
            content
            courseData {
              title
              description
              price
              fareHabor
              courseMinimumAge
              coursePrerequisites
              courseAcademicSessions
              coursePoolSessions
              courseDives
            }
            header
            subheader
            imageFileNameDetail
            imageFileName
          }
          coursesKnowledgeHeader
          coursesKnowledgeSubheader
          coursesKnowledge {
            content
            courseData {
              title
              description
              price
              fareHabor
              courseMinimumAge
              coursePrerequisites
              courseAcademicSessions
              coursePoolSessions
              courseDives
            }
            header
            subheader
            imageFileNameDetail
            imageFileName
          }
          coursesProfessionalHeader
          coursesProfessionalSubheader
          coursesProfessional {
            content
            courseData {
              title
              description
              price
              fareHabor
              courseMinimumAge
              coursePrerequisites
              courseAcademicSessions
              coursePoolSessions
              courseDives
            }
            header
            subheader
            imageFileNameDetail
            imageFileName
          }
          coursesSnorkelingHeader
          coursesSnorkelingSubheader
          coursesSnorkeling {
            content
            courseData {
              title
              description
              price
              fareHabor
              courseMinimumAge
              coursePrerequisites
              courseAcademicSessions
              coursePoolSessions
              courseDives
            }
            header
            subheader
            imageFileNameDetail
            imageFileName
          }
          coursesOthersHeader
          coursesOthersSubheader
          coursesOthers {
            content
            courseData {
              title
              description
              price
              fareHabor
              courseMinimumAge
              coursePrerequisites
              courseAcademicSessions
              coursePoolSessions
              courseDives
            }
            header
            subheader
            imageFileNameDetail
            imageFileName
          }
          privacyText
          privacyContent
          cancellationPolicyText
          cancellationPolicyContent
          services {
            content
            header
            iconName
            imageFileName
            jumpToAnchor
            jumpToAnchorIcon
            jumpToAnchorText
          }
          social {
            facebook
            github
            linkedin
            medium
            twitter
            instagram
            youtube
            tripadvisor
          }
          teamMember {
            header
            imageFileName
            social {
              facebook
              github
              linkedin
              medium
              twitter
              instagram
              youtube
            }
            subheader
            disabled
          }
          address
          telephone
          termsHref
          termsText
          title
          spots {
            content
            header
            imageContent
            imageFileName
            subheader
            maxDepth
            wrecks {
              imageFileName
              header
              shipType
              targetUrl
              maxDepth
            }
          }
        }
        fields {
          fileName
          directoryName
        }
      }
    }
  }
`;

const IndexPage = ({location, data, pathContext: {langKey, defaultLang, langTextMap}}) => {
    // useEffect(() => {
    //     // Matomo Tracking Code
    //     // eslint-disable-next-line no-multi-assign
    //     const _paq = window._paq = window._paq || [];
    //     /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
    //     _paq.push(['trackPageView']);
    //     _paq.push(['enableLinkTracking']);
    //     (function() {
    //         const u = "https://www.wedive.pt/analytics/";
    //         _paq.push(['setTrackerUrl', `${u}matomo.php`]);
    //         _paq.push(['setSiteId', '69']);
    //         const d = document; const g = d.createElement('script'); const s = d.getElementsByTagName('script')[0];
    //         g.async=true; g.src=`${u}matomo.js`; s.parentNode.insertBefore(g,s);
    //     })();
    // }, []);

    useEffect(() => {
        const script = document.createElement('script');
        script.defer = true;
        script.src = '/u/s.js';
        script.setAttribute('data-website-id', '01ff708c-5a2b-48d4-949a-3c9d3b78077f');
        script.setAttribute('data-host-url', '/');

        document.head.appendChild(script);

        return () => {
            document.head.removeChild(script);
        };
    }, []);

    const {
        site: {
            siteMetadata: {keywords, description},
        },
        allMarkdownRemark: {nodes},
    } = data;

    const {globalsNode, topNode, navBarNode, anchors, footerNode, sectionsNodes, trackingId} = breakDownAllNodes(nodes);

    let langSelectorPart;
    if (langTextMap != null && Object.keys(langTextMap).length > 1) {
        langSelectorPart = (
            <LanguageSelector langKey={langKey} defaultLang={defaultLang} langTextMap={langTextMap}/>
        );
    }

    return (
        <>
            <SEO lang={langKey} title="Diving the Algarve" keywords={keywords} description={description}/>
            <Navbar
                anchors={anchors}
                frontmatter={navBarNode.frontmatter}
                extraItems={langSelectorPart}
                globals={globalsNode.frontmatter}
            />
            <Top frontmatter={topNode.frontmatter} globals={globalsNode.frontmatter}/>
            {
                // dynamically import sections
                sectionsNodes.map(({frontmatter, fields: {fileName}}, ind) => {
                    const sectionComponentName = fileNameToSectionName(fileName);
                    const SectionComponent = Sections[sectionComponentName];

                    return SectionComponent ? (
                        <SectionComponent
                            key={sectionComponentName}
                            className={ind % 2 === 1 ? 'bg-light' : null}
                            frontmatter={frontmatter}
                            globals={globalsNode.frontmatter}
                            location={location}
                        />
                    ) : null;
                })
            }
            <Footer frontmatter={footerNode.frontmatter} globals={globalsNode.frontmatter} location={location}/>
            {/* <CookieConsentComponent/> */}
        </>
    );
};

IndexPage.propTypes = {
    location: PropTypes.object.isRequired,
    data: PropTypes.object.isRequired,
    pathContext: PropTypes.object,
};

IndexPage.defaultProps = {
    pathContext: {
        langKey: 'pt',
        defaultLang: 'pt',
        langTextMap: {},
    },
};

export default IndexPage;
